import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

// Mapiranje country code na prefiks
const COUNTRY_PREFIXES = {
  rs: '381', // Serbia
  ba: '387', // Bosnia and Herzegovina
  hr: '385', // Croatia
  me: '382', // Montenegro
  mk: '389', // North Macedonia
  si: '386', // Slovenia
  // Dodaj više država po potrebi
  us: '1',
  gb: '44',
  de: '49',
  fr: '33',
  it: '39',
  es: '34',
  // ... dodaj više po potrebi
};

/**
 * Jednostavna PhoneInput komponenta bez zastavica
 * - Koristi default prefiks na osnovu države firme (samo pri inicijalizaciji)
 * - '+' znak mora uvek da ostane (jedino obavezno)
 * - Može da se unese BILO KOJI prefiks posle '+' (nema ograničenja)
 * - Može da se brišu i menjaju sve cifre, ali '+' mora da ostane
 * @param {string} value - Vrednost telefonskog broja
 * @param {function} onChange - Callback funkcija koja prima (value)
 * @param {string} defaultCountryCode - Default country code (npr. 'rs', 'ba')
 * @param {string} className - Dodatne CSS klase
 * @param {string} placeholder - Placeholder tekst
 * @param {boolean} required - Da li je polje obavezno
 * @param {function} onBlur - Callback za blur event
 * @param {function} onFocus - Callback za focus event
 * @param {boolean} disabled - Da li je polje onemogućeno
 */
const CustomPhoneInput = ({
  value = '',
  onChange,
  defaultCountryCode = null, // Ako nije prosleđen, koristi se iz user/company data
  className = '',
  placeholder = 'Enter phone number',
  required = false,
  onBlur,
  onFocus,
  disabled = false,
  ...otherProps
}) => {
  const { user } = useSelector((state) => state.auth);
  const inputRef = useRef(null);
  
  // Odredi default country code
  const getDefaultCountryCode = () => {
    if (defaultCountryCode) return defaultCountryCode.toLowerCase();
    // Pokušaj da uzmeš iz user/company data
    const userCountryCode = user?.result?.countryCode || user?.countryCode;
    if (userCountryCode) return userCountryCode.toLowerCase();
    return 'rs'; // Default na Srbiju
  };

  const countryCode = getDefaultCountryCode();
  const defaultPrefix = `+${COUNTRY_PREFIXES[countryCode] || '381'}`;

  const handleChange = (e) => {
    let inputValue = e.target.value;
    const originalValue = inputValue;
    
    console.log('📝 handleChange - POČETAK:', {
      originalValue: originalValue,
      inputValue: inputValue
    });
    
    // Ukloni sve osim cifara i +
    inputValue = inputValue.replace(/[^\d+]/g, '');
    
    console.log('📝 handleChange - Posle uklanjanja nevalidnih:', {
      inputValue: inputValue
    });
    
    // Obavezuj da počinje sa + (jedino obavezno)
    if (!inputValue.startsWith('+')) {
      // Ako nema + na početku, dodaj ga
      if (inputValue.length > 0) {
        inputValue = '+' + inputValue.replace(/\+/g, '');
      } else {
        // Ako je prazno, postavi samo '+'
        inputValue = '+';
      }
      console.log('📝 handleChange - Dodat + na početak:', {
        inputValue: inputValue
      });
    } else {
      // Ako već počinje sa +, ukloni sve dodatne + znakove
      const plusCount = (inputValue.match(/\+/g) || []).length;
      if (plusCount > 1) {
        inputValue = '+' + inputValue.replace(/\+/g, '');
        console.log('📝 handleChange - Uklonjeni dodatni + znakovi:', {
          inputValue: inputValue
        });
      }
    }
    
    // Dozvoli bilo koji prefiks posle '+' - nema ograničenja
    // Ako je samo '+', dozvoli to (korisnik može da unese bilo koji prefiks)
    console.log('📝 handleChange - FINALNA VREDNOST:', {
      inputValue: inputValue,
      originalValue: originalValue,
      changed: inputValue !== originalValue
    });
    
    if (onChange) {
      console.log('📞 handleChange - POZIVAM onChange sa:', inputValue);
      onChange(inputValue);
      console.log('✅ handleChange - onChange pozvan');
    } else {
      console.warn('⚠️ handleChange - onChange NIJE DEFINISAN!');
    }
  };

  const handleKeyDown = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const value = input.value || '';
    const hasSelection = cursorPosition !== selectionEnd;
    
    // LOG: Prikaži informacije o pokušaju brisanja
    if (e.key === 'Backspace' || e.key === 'Delete') {
      console.log('🔍 POKUŠAJ BRISANJA:', {
        key: e.key,
        value: value,
        cursorPosition: cursorPosition,
        selectionEnd: selectionEnd,
        hasSelection: hasSelection,
        selectionText: hasSelection ? value.substring(cursorPosition, selectionEnd) : 'nema',
        startsWithPlus: value.startsWith('+')
      });
    }
    
    // Spreči brisanje '+' znaka - jedino ograničenje
    if (e.key === 'Backspace') {
      // Ako je kursor na poziciji 1 (posle '+'), i pokušava da obriše '+'
      if (cursorPosition === 1 && value.startsWith('+')) {
        console.log('❌ SPRECENO: Backspace na poziciji 1 (posle +)');
        e.preventDefault();
        return;
      }
      // Ako je selektovan ceo tekst uključujući '+', spreči brisanje
      if (hasSelection && cursorPosition === 0 && selectionEnd > 0 && value.startsWith('+')) {
        console.log('❌ SPRECENO: Backspace sa selektovanim tekstom koji uključuje +');
        e.preventDefault();
        // Postavi kursor posle '+' i zadrži ostatak teksta
        const remainingText = value.substring(1);
        const newValue = '+' + remainingText;
        console.log('✅ Zadržan +, ostatak:', remainingText);
        if (onChange) {
          onChange(newValue);
        }
        setTimeout(() => {
          input.setSelectionRange(1, 1);
        }, 0);
        return;
      }
      // Ako je selektovan deo koji uključuje '+', spreči brisanje
      if (hasSelection && cursorPosition === 0 && value.startsWith('+')) {
        console.log('❌ SPRECENO: Backspace sa selektovanim delom koji uključuje +');
        e.preventDefault();
        // Zadrži '+' i ostatak teksta posle selektovanog dela
        const endPos = Math.max(1, selectionEnd);
        const remainingText = value.substring(endPos);
        const newValue = '+' + remainingText;
        console.log('✅ Zadržan +, ostatak posle selektovanog:', remainingText);
        if (onChange) {
          onChange(newValue);
        }
        setTimeout(() => {
          input.setSelectionRange(1, 1);
        }, 0);
        return;
      }
      console.log('✅ DOZVOLJENO: Backspace - ne utiče na +');
    }
    
    // Za Delete taster
    if (e.key === 'Delete') {
      // Ako je kursor na poziciji 0 (na '+'), spreči brisanje
      if (cursorPosition === 0 && value.startsWith('+')) {
        console.log('❌ SPRECENO: Delete na poziciji 0 (na +)');
        e.preventDefault();
        return;
      }
      // Ako je selektovan deo koji uključuje '+', spreči brisanje
      if (hasSelection && cursorPosition === 0 && value.startsWith('+')) {
        console.log('❌ SPRECENO: Delete sa selektovanim delom koji uključuje +');
        e.preventDefault();
        const endPos = Math.max(1, selectionEnd);
        const remainingText = value.substring(endPos);
        const newValue = '+' + remainingText;
        console.log('✅ Zadržan +, ostatak posle selektovanog:', remainingText);
        if (onChange) {
          onChange(newValue);
        }
        setTimeout(() => {
          input.setSelectionRange(1, 1);
        }, 0);
        return;
      }
      console.log('✅ DOZVOLJENO: Delete - ne utiče na +');
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    
    // Ukloni sve osim cifara i +
    let cleaned = pastedText.replace(/[^\d+]/g, '');
    
    // Obavezuj da počinje sa + (jedino obavezno)
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned.replace(/\+/g, '');
    } else {
      // Ukloni sve + osim prvog
      cleaned = '+' + cleaned.replace(/\+/g, '');
    }
    
    // Dozvoli bilo koji prefiks - nema ograničenja
    // Ako je samo '+', dozvoli to
    if (onChange) {
      onChange(cleaned);
    }
    
    // Postavi kursor na kraj
    setTimeout(() => {
      if (inputRef.current) {
        const len = cleaned.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }, 0);
  };

  // Ako je value prazan, koristi default prefiks samo pri inicijalizaciji
  // Inače koristi value (može biti bilo koji prefiks)
  const displayValue = value || defaultPrefix;

  console.log('🎨 RENDER:', {
    value: value,
    defaultPrefix: defaultPrefix,
    displayValue: displayValue,
    valueType: typeof value,
    valueLength: value ? value.length : 0
  });

  return (
    <input
      ref={inputRef}
      type="tel"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      className={`w-full border border-gray-600 rounded px-3 py-[0px] text-sm text-white bg-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-shadow h-[23px] ${className}`}
      autoComplete="tel"
      {...otherProps}
    />
  );
};

export default CustomPhoneInput;
